import React from 'react';
import { Plus } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

import { calMultiDatesPrices } from '../../../../helper';
import { useProgramForm } from '../../../../context/ProgramFormContext';
import SectionCard, { SubGroup } from '../ui/SectionCard';
import Field from '../ui/Field';
import { NumberInput, SelectInput } from '../ui/Inputs';
import QuestionCard from '../QuestionCard';
import { s, brand } from '../theme';

/**
 * Section 7 — Extra. Ports `AddProgramModal.js:2456-2722`.
 *
 * Discounts, visibility, and the custom question list. Nothing here is required, which is
 * why the whole section is hidden by the "show required only" filter.
 *
 * `isfeatured` is part of the payload but has no control on this form and never has —
 * it is carried through untouched.
 */

const amountOpt = [
    { value: 1, label: 'Amount' },
    { value: 2, label: 'Percentage' },
];

// Design labels for `isprivate`. The values match `helper.js` `trueFalse2` — now numbers,
// as the API stores them (`programModel.js:140`). They were strings, and the lookup below
// is strict, so `'0' === 0` was false and the control fell back to its placeholder on every
// existing program whatever the stored value. A director read that as "it didn't save".
const isPrivateOpt = [
    { value: 1, label: 'Yes — invite only' },
    { value: 0, label: 'No — publicly listed' },
];

/**
 * The three discount rows, one per `userInput` pair.
 *
 * `name` is the row label under APPLIES TO; the `*Label` values are the old form's field
 * labels, kept verbatim so the row still names itself to a screen reader once the visible
 * label is hidden behind the column header.
 */
const discountRows = [
    {
        name: 'Program discount',
        typeKey: 'discountType',
        typeLabel: 'Discount type',
        amountKey: 'discount',
        amountLabel: 'Discount',
    },
    {
        name: 'Sibling discount',
        typeKey: 'siblingDiscountType',
        typeLabel: 'Sibling Discount Type',
        amountKey: 'siblingDiscount',
        amountLabel: 'Sibling Discount',
    },
    {
        name: 'Multi-program discount',
        typeKey: 'multiProgramDiscountType',
        typeLabel: 'Multi Program Discount Type',
        amountKey: 'multiProgramDiscount',
        amountLabel: 'Multi Program Discount',
    },
];

const money = (value, type) =>
    type === 1 ? `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : `${value}%`;

const Extra = () => {
    const {
        userInput,
        additionalDates,
        cmQuestions,
        setCmQuestions,
        cmQuestionsErr,
        setCmQuestionsErr,
        handleSelect,
        handleNumberChange,
        fieldStatus,
    } = useProgramForm();

    // A new question is only allowed once the previous one has a title
    // (`AddProgramModal.js:1344-1368`).
    const handleAddQues = () => {
        const temp = [...cmQuestions];

        if (cmQuestions.length === 0) {
            temp.push({
                questionId: uuidv4(),
                questionType: '',
                title: '',
                subTitle: '',
                optionVal: '',
                options: [],
                isRequred: 2,
                isOtherOption: 2,
            });
        } else if (temp[temp.length - 1]?.title === '') {
            setCmQuestionsErr('last field can not be empty');
            return;
        } else {
            temp.push({
                questionId: uuidv4(),
                questionType: '',
                title: '',
                subTitle: '',
                optionVal: '',
                options: [],
                isRequred: 2,
                isOtherOption: 2,
            });
            setCmQuestionsErr('');
        }

        setCmQuestions(temp);
    };

    const discountDisabled = !(userInput.registrationFee || calMultiDatesPrices(additionalDates));

    return (
        <SectionCard
            id="extra"
            step={7}
            title="Extra"
            description="Discounts, visibility and custom registration questions"
        >
            <SubGroup title="Discounts" description="Leave blank to skip a discount type.">
                <div className="pf-head pf-discounts">
                    <span style={s.groupTitle}>Applies to</span>
                    <span style={s.groupTitle}>Type</span>
                    <span style={s.groupTitle}>Value</span>
                </div>

                {discountRows.map(row => {
                    // Only the program discount is gated on a price existing; the sibling and
                    // multi-program amounts never have been.
                    const gated = row.amountKey === 'discount';
                    const type = userInput[row.typeKey];

                    return (
                        <div key={row.amountKey} className="pf-row pf-discounts">
                            <span style={{ ...s.label, alignSelf: 'center' }}>{row.name}</span>

                            <Field
                                className="pf-hide-label"
                                label={row.typeLabel}
                                htmlFor={row.typeKey}
                            >
                                <SelectInput
                                    id={row.typeKey}
                                    pHolder="Select a type"
                                    change={e => handleSelect(e, row.typeKey)}
                                    value={amountOpt.filter(item => item.value === type)}
                                    options={amountOpt}
                                />
                            </Field>

                            <Field
                                className="pf-hide-label"
                                label={row.amountLabel}
                                htmlFor={row.amountKey}
                                hint={gated && discountDisabled ? 'Set a standard price first.' : ''}
                                {...(gated ? fieldStatus('discount') : {})}
                            >
                                <NumberInput
                                    /* rc-input-number keeps the formatted text as its own
                                       state and only re-derives it when `value` or `precision`
                                       change (`rc-input-number/es/InputNumber.js:387-403`) — a
                                       changed `formatter` alone never repaints, so an amount
                                       already on screen would keep the `$` after the type was
                                       switched to Percentage. Remounting on the type is what
                                       re-runs the formatter, and it is free here: the type
                                       lives in the select beside it, so this input never holds
                                       focus at the moment it changes. The `formatter={money}`
                                       calls in `sections/Price.js` are a stable module-level
                                       function and so need none of this. */
                                    key={`${row.amountKey}-${type}`}
                                    id={row.amountKey}
                                    disabled={gated && discountDisabled}
                                    change={e => handleNumberChange(e, row.amountKey)}
                                    formatter={value => money(value, type)}
                                    value={userInput[row.amountKey]}
                                    min={0}
                                    precision={userInput[row.amountKey] ? 2 : 0}
                                    pHolder={type === 2 ? '0%' : '0.00'}
                                    max={type === 1 ? Number.MAX_VALUE : 100}
                                />
                            </Field>
                        </div>
                    );
                })}
            </SubGroup>

            <div className="mt-4">
                <SubGroup title="Visibility">
                    <div className="row">
                        <Field
                            className="col-md-6 mb-3"
                            label="Is Private"
                            htmlFor="isprivate"
                            hint="Private programs are only reachable with a direct link."
                        >
                            <SelectInput
                                id="isprivate"
                                pHolder="Is Private"
                                change={e => handleSelect(e, 'isprivate')}
                                value={isPrivateOpt.filter(el => el.value === userInput.isprivate)}
                                options={isPrivateOpt}
                            />
                        </Field>
                    </div>
                </SubGroup>
            </div>

            <div className="mt-4">
                <SubGroup
                    title="Custom questions"
                    description="Asked during registration, in the order listed."
                >
                    {cmQuestions.map((item, i) => (
                        <QuestionCard key={item.questionId || i} item={item} index={i} />
                    ))}

                    {cmQuestionsErr ? (
                        <p style={{ ...s.errorText, marginBottom: '8px' }}>{cmQuestionsErr}</p>
                    ) : ''}

                    <button
                        type="button"
                        style={{ ...s.buttonSecondary, borderColor: brand[300] }}
                        onClick={handleAddQues}
                    >
                        <Plus size={14} aria-hidden="true" />
                        Add Question
                    </button>
                </SubGroup>
            </div>
        </SectionCard>
    );
};

export default Extra;
